import { Box, Button, Icon, Typography } from "@outschool/backpack";
import { REFER_CREDIT_CENTS, centsToDollars } from "@outschool/business-rules";
import {
  OutreachReferralsQuery,
  OutreachReferralsQueryVariables,
  ParentReferralStatus,
  ReferralsQuery,
  ReferralsQueryVariables
} from "@outschool/gql-frontend-generated";
import { faCheck, faChevronRight, faSpinner } from "@outschool/icons";
import {
  TFunction,
  Trans,
  renderCurrencyString,
  useTranslation
} from "@outschool/localization";
import { useQueryWithPreviousData } from "@outschool/ui-apollo";
import {
  Loading,
  Modal,
  ModalProps
} from "@outschool/ui-legacy-component-library";
import React, { useState } from "react";

import * as Env from "../../shared/Env";
import * as Routes from "../../shared/Routes";
import ErrorMessage from "./ErrorMessage";
import Link from "./Link";
import {
  OutreachUserReferrals,
  ReferredParentsList,
  UserReferrals,
  referredOutreachParentStatusToLabel
} from "./Referrals";

const ReferralsModal = (
  props: Omit<ModalProps, "children" | "sx"> & { isEducator: boolean }
) => {
  const { t } = useTranslation("client\\components\\ReferralsModal");
  const { isEducator, ...modalProps } = props;

  return (
    <Modal
      sx={{
        display: "flex",
        flexFlow: "column",
        alignItems: "center",
        textAlign: "center"
      }}
      {...modalProps}
    >
      <Typography
        variant="h4"
        sx={{
          marginBottom: "0.5em"
        }}
      >
        {t`Referral Status`}
      </Typography>
      <Box
        component="p"
        sx={{
          marginBottom: "2em"
        }}
      >
        {isEducator ? (
          <Trans t={t}>
            You’ll receive your referral bonus{" "}
            <strong>
              once your friend’s plan renews and they’ve taken their first
              class.
            </strong>
            <br />
            <br />
            Families using state funds may receive a $50 voucher instead.
          </Trans>
        ) : (
          <Trans t={t}>
            To receive credits, your friend needs to{" "}
            <strong>sign up and attend their first class</strong>.
          </Trans>
        )}
      </Box>
      <ReferralsModalListing t={t} isEducator={isEducator} />
    </Modal>
  );
};
export default ReferralsModal;

const ReferralsModalListing = ({
  t,
  isEducator
}: {
  t: TFunction;
  isEducator: boolean;
}) => {
  const [loadingMore, setLoadingMore] = useState(false);

  const { loading, data, error } = useQueryWithPreviousData<
    ReferralsQuery,
    ReferralsQueryVariables
  >(UserReferrals, {
    fetchPolicy: "cache-and-network",
    skip: isEducator
  });

  const {
    loading: outreachLoading,
    data: outreachData,
    error: outreachError,
    fetchMore
  } = useQueryWithPreviousData<
    OutreachReferralsQuery,
    OutreachReferralsQueryVariables
  >(OutreachUserReferrals, {
    fetchPolicy: "cache-and-network",
    skip: !isEducator,
    variables: {}
  });

  const handleLoadMore = async () => {
    if (!outreachData?.currentUser?.outreachReferrals?.pageInfo?.hasNextPage) {
      return;
    }

    setLoadingMore(true);
    try {
      await fetchMore({
        variables: {
          after: outreachData.currentUser.outreachReferrals.pageInfo.lastCursor
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.currentUser?.outreachReferrals) {
            return prev;
          }

          const prevEdges = prev.currentUser?.outreachReferrals?.edges || [];
          const newEdges =
            fetchMoreResult.currentUser.outreachReferrals.edges || [];

          return {
            ...prev,
            currentUser: {
              ...prev.currentUser!,
              outreachReferrals: {
                ...fetchMoreResult.currentUser.outreachReferrals,
                edges: [...prevEdges, ...newEdges]
              }
            }
          };
        }
      });
    } finally {
      setLoadingMore(false);
    }
  };

  // Extract referral nodes from connection structure
  const outreachReferrals = isEducator
    ? outreachData?.currentUser?.outreachReferrals?.edges?.map(
        edge => edge.node
      ) ?? []
    : [];

  const referredParents = isEducator
    ? outreachReferrals
    : data?.currentUser?.referredParents ?? [];

  if (loading || outreachLoading) {
    return <Loading />;
  } else if (error || outreachError) {
    return (
      <ErrorMessage value={error} showStatusPageLink={Env.IS_READ_ONLY_MODE} />
    );
  } else {
    return (
      <>
        {!isEducator && (
          <>
            <div>{t`My total earnings`}</div>
            <Box
              sx={{
                fontWeight: "fontWeightBold",
                fontSize: "2em"
              }}
            >
              {"$"}
              {centsToDollars(data?.currentUser?.totalReferralEarnings ?? 0)}
            </Box>
            <Box
              sx={{
                fontSize: "0.875em",
                marginBottom: "2em"
              }}
            >
              <Link to={Routes.transactionsPath()}>
                {t`See my transactions`} <Icon icon={faChevronRight} />
              </Link>
            </Box>
          </>
        )}
        <Box
          sx={{
            overflowY: "auto",
            width: "100%",
            maxHeight: "300px"
          }}
        >
          <ReferredParentsList
            referredParents={referredParents}
            t={t}
            omitTitle={true}
            sx={{
              width: "100%",
              thead: { backgroundColor: "white", position: "sticky", top: 0 }
            }}
            makeLabel={
              isEducator
                ? referredOutreachParentStatusToLabel
                : makeReferralLabel
            }
          />
          {isEducator &&
            outreachData?.currentUser?.outreachReferrals?.pageInfo
              ?.hasNextPage && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "1em"
                }}
              >
                <Button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  variant="outlined"
                  size="small"
                >
                  {loadingMore ? (
                    <>
                      <Icon icon={faSpinner} spin /> {t`Loading...`}
                    </>
                  ) : (
                    t`Load More`
                  )}
                </Button>
              </Box>
            )}
        </Box>
      </>
    );
  }
};

const makeReferralLabel = (
  status: ParentReferralStatus,
  t: TFunction,
  amountCents?: number
) => {
  switch (status) {
    case ParentReferralStatus.SignedUp:
      return <>{t`Hasn’t taken a class yet`}</>;
    case ParentReferralStatus.CreditEarned:
    case ParentReferralStatus.CreditClaimed:
      return (
        <>
          {t("{{creditString}} earned!", {
            creditString: renderCurrencyString({
              priceInCents: amountCents ? amountCents : REFER_CREDIT_CENTS,
              showCurrencyCode: false
            })
          })}{" "}
          <Icon icon={faCheck} color="primary" />
        </>
      );
  }
};
